import { useMutation, useQuery } from "@tanstack/react-query"
import { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import GameScreenLayout from "@/components/GameScreenLayout"
import { getRankExam, submitRankExam } from "@/api/gameApi"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"

export default function RankExamPage() {
  const { cardId } = useParams<{ cardId: string }>()
  const navigate = useNavigate()
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const { syncGameState, bumpCards } = useGuardiansPassport()

  const examQ = useQuery({
    queryKey: ["rank-exam", cardId],
    queryFn: () => getRankExam(cardId!),
    enabled: Boolean(cardId),
  })

  const submit = useMutation({
    mutationFn: () =>
      submitRankExam(cardId!, examQ.data!.questions, answers),
    onSuccess: async (res) => {
      await syncGameState()
      bumpCards()
      if (res.passed) navigate(`/play/cards/${cardId}`)
    },
  })

  const questions = examQ.data?.questions ?? []

  if (examQ.data?.blockedUntil) {
    const until = new Date(examQ.data.blockedUntil)
    if (until.getTime() > Date.now()) {
      return (
        <GameScreenLayout title="Экзамен ранга" backTo={`/play/cards/${cardId}`}>
          <p className="text-sm text-guardians-muted">
            Повтор после {until.toLocaleString("ru-RU")}
          </p>
        </GameScreenLayout>
      )
    }
  }

  return (
    <GameScreenLayout title="Экзамен ранга" backTo={`/play/cards/${cardId}`}>
      {examQ.isLoading ? (
        <p className="text-sm text-guardians-muted">Загрузка…</p>
      ) : (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault()
            submit.mutate()
          }}
        >
          {questions.map((q, idx) => (
            <fieldset key={q.id} className="rounded-xl border border-white/10 p-3">
              <legend className="text-sm font-medium">
                {idx + 1}. {q.question}
              </legend>
              <ul className="mt-2 space-y-1">
                {q.options.map((opt) => (
                  <li key={opt}>
                    <label className="flex gap-2 text-sm">
                      <input
                        type="radio"
                        name={q.id}
                        checked={answers[q.id] === opt}
                        onChange={() =>
                          setAnswers((prev) => ({ ...prev, [q.id]: opt }))
                        }
                      />
                      {opt}
                    </label>
                  </li>
                ))}
              </ul>
            </fieldset>
          ))}
          <button
            type="submit"
            disabled={
              submit.isPending ||
              questions.some((q) => !answers[q.id]?.trim())
            }
            className="w-full rounded-xl bg-guardians-accent py-3 text-guardians-bg disabled:opacity-40"
          >
            Сдать экзамен
          </button>
        </form>
      )}

      {submit.data ? (
        <p className="mt-3 text-sm">
          {submit.data.passed ? "Сдано!" : "Не сдано"} · {submit.data.correctCount}/
          {submit.data.totalCount}
        </p>
      ) : null}

      {submit.isError ? (
        <p className="mt-3 text-sm text-red-300">
          {getGameApiErrorMessage(submit.error, "Экзамен не удался")}
        </p>
      ) : null}
    </GameScreenLayout>
  )
}
